/**
 * Pegar montos en los inputs numéricos (xcons-docs#610, punto 5).
 *
 * El `InputNumber` de antd y el `<input type="number">` sólo entienden el punto como
 * decimal. El admin muestra los montos en formato argentino (`$ 10.148,56`), así que
 * pegar uno de ésos —o uno copiado de un Excel— se leía mal sin avisar: `1.500` quedaba
 * en 1,5 y `10,5` en 105.
 *
 * Se resuelve en un solo lugar, al pegar, y no con un `parser` en cada input: tipear no
 * cambia, y cubre también los inputs que se agreguen después.
 */

/**
 * El texto pegado como número con punto decimal (`"10148.56"`), o `null` si no es un
 * número. Reglas:
 * - se descarta todo lo que no sea dígito, `.`, `,` o `-` (`$`, `%`, espacios, `ARS`);
 * - con punto y coma, el último de los dos es el decimal;
 * - con sólo coma: una es el decimal, varias son miles;
 * - con sólo punto: varios son miles, y uno seguido de exactamente 3 dígitos también
 *   (`1.500`). Si no, es el decimal (`2537.14`, como los inputs muestran los precios).
 */
export function normalizePastedNumber(text: string): string | null {
  const raw = text.trim().replace(/[^\d.,-]/g, "");
  const negative = raw.startsWith("-");
  const body = negative ? raw.slice(1) : raw;
  // Un guión en el medio es un rango o un teléfono, no un número.
  if (body.includes("-")) return null;
  if (!/\d/.test(body)) return null;

  const lastDot = body.lastIndexOf(".");
  const lastComma = body.lastIndexOf(",");
  const dots = body.split(".").length - 1;
  const commas = body.split(",").length - 1;

  let normalized: string;
  if (dots && commas) {
    const decimal = lastDot > lastComma ? "." : ",";
    const thousands = decimal === "." ? "," : ".";
    if (body.split(decimal).length - 1 > 1) return null;
    normalized = body.split(thousands).join("").replace(decimal, ".");
  } else if (commas) {
    normalized = commas === 1 ? body.replace(",", ".") : body.split(",").join("");
  } else if (dots) {
    const thousands = dots > 1 || /^\d{1,3}\.\d{3}$/.test(body);
    normalized = thousands ? body.split(".").join("") : body;
  } else {
    normalized = body;
  }

  if (!/^\d*\.?\d+$|^\d+\.$/.test(normalized)) return null;
  return (negative ? "-" : "") + normalized;
}

const isNumberInput = (el: EventTarget | null): el is HTMLInputElement =>
  el instanceof HTMLInputElement &&
  (el.type === "number" || !!el.closest(".ant-input-number"));

function onPaste(event: ClipboardEvent) {
  const input = event.target;
  if (!isNumberInput(input) || input.readOnly || input.disabled) return;
  const text = event.clipboardData?.getData("text") ?? "";
  const number = normalizePastedNumber(text);
  if (number === null || number === text) return;

  event.preventDefault();
  // Lo pegado reemplaza la selección, como un paste normal. `selectionStart` no existe en
  // `type="number"`: ahí se reemplaza el valor entero.
  let next = number;
  let caret: number | null = null;
  if (input.type !== "number" && input.selectionStart !== null) {
    const start = input.selectionStart;
    const end = input.selectionEnd ?? start;
    next = input.value.slice(0, start) + number + input.value.slice(end);
    caret = start + number.length;
  }
  // El setter nativo + `input` es lo que hace que React (y el InputNumber) se enteren.
  Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(
    input,
    next,
  );
  input.dispatchEvent(new Event("input", { bubbles: true }));
  if (caret !== null) input.setSelectionRange(caret, caret);
}

/** Se llama una vez al montar la app. Devuelve la función que lo desinstala. */
export function installNumberPaste(): () => void {
  document.addEventListener("paste", onPaste, true);
  return () => document.removeEventListener("paste", onPaste, true);
}
